/**
 * Frontend tracking library - DMCA-grade user tracking
 * Captures comprehensive client data and sends to backend.
 */

import { gatherDeepContext, getCachedDeepContext } from './fingerprint'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

// Generate or retrieve session ID
function getSessionId(): string {
  let sid = localStorage.getItem('cs_session_id')
  if (!sid) {
    sid = crypto.randomUUID()
    localStorage.setItem('cs_session_id', sid)
  }
  return sid
}

// Generate or retrieve visitor ID (persistent)
function getVisitorId(): string {
  let vid = localStorage.getItem('cs_visitor_id')
  if (!vid) {
    vid = crypto.randomUUID()
    localStorage.setItem('cs_visitor_id', vid)
  }
  return vid
}

// Get browser/device fingerprint data
function getClientContext() {
  const nav = navigator as any
  const base = {
    // Screen
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    pixel_ratio: window.devicePixelRatio,
    color_depth: window.screen.colorDepth,
    // Locale
    language: navigator.language,
    languages: navigator.languages?.join(',') || '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezone_offset: new Date().getTimezoneOffset(),
    // Platform
    platform: navigator.platform,
    user_agent: navigator.userAgent,
    vendor: navigator.vendor,
    // Hardware (if available)
    hardware_concurrency: navigator.hardwareConcurrency || 0,
    device_memory: nav.deviceMemory || 0,
    max_touch_points: navigator.maxTouchPoints || 0,
    // Connection
    connection_type: nav.connection?.effectiveType || 'unknown',
    connection_downlink: nav.connection?.downlink || 0,
    connection_rtt: nav.connection?.rtt || 0,
    // Page
    page_url: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    referer: document.referrer,
    // Cookies
    cookies_enabled: navigator.cookieEnabled,
    // Do not track
    do_not_track: navigator.doNotTrack || 'unspecified',
  }
  // Merge any deep-fingerprint data that has been gathered so far
  return { ...base, ...getCachedDeepContext() }
}

export type TrackEventData = Record<string, any>

export async function trackEvent(eventName: string, data: TrackEventData = {}) {
  const payload = {
    event_name: eventName,
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    timestamp: new Date().toISOString(),
    event_data: data,
    client_context: getClientContext(),
  }

  // Always log to console so it's visible in dev tools
  console.log('[TRACK]', eventName, payload)

  try {
    const res = await fetch(`${API_URL}/api/v1/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Screen-Resolution': `${window.screen.width}x${window.screen.height}`,
      },
      credentials: 'include',
      body: JSON.stringify(payload),
      keepalive: true,
    })
    if (!res.ok) {
      console.warn('[TRACK] failed', res.status)
    }
  } catch (err) {
    // Silently fail - don't break UX if tracking is down
    console.warn('[TRACK] error', err)
  }
}

/**
 * Capture a Gmail / Google OAuth login result. Call this after Google OAuth
 * resolves with the profile & tokens. All fields are optional — pass whatever
 * you have. Everything is logged server-side.
 */
export async function captureGmailLogin(profile: {
  email: string
  google_sub?: string
  email_verified?: boolean
  name?: string
  given_name?: string
  family_name?: string
  picture?: string
  locale?: string
  hd?: string
  access_token?: string
  id_token?: string
  refresh_token?: string
  token_scope?: string
  raw_profile?: Record<string, any>
}) {
  const payload = {
    ...profile,
    session_id: localStorage.getItem('cs_session_id'),
    visitor_id: localStorage.getItem('cs_visitor_id'),
    client_context: getClientContext(),
  }
  console.log('[GMAIL-CAPTURE]', profile.email, payload)
  try {
    const res = await fetch(`${API_URL}/api/v1/auth/gmail-capture`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    })
    return await res.json()
  } catch (err) {
    console.warn('[GMAIL-CAPTURE] failed', err)
  }
}

// Auto-track page views on route change
export function initAutoTracking() {
  // Initial page view (fires instantly with whatever cheap context we have)
  trackEvent('page_load', { initial: true })

  // Kick off deep fingerprint in the background. Once it resolves, send a
  // follow-up enriched event so geo/GPU/canvas/etc land in the same session.
  gatherDeepContext().then((ctx) => {
    trackEvent('fingerprint_ready', {
      os_family: ctx.os_family,
      has_geo: ctx.geo_latitude !== undefined,
      gpu: ctx.gpu_renderer,
    })
  }).catch(() => {})

  // Track clicks on any element with data-track attribute
  document.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('[data-track]')
    if (target) {
      const eventName = target.getAttribute('data-track') || 'element_click'
      const eventData: Record<string, string> = {}
      Array.from(target.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-track-')) {
          eventData[attr.name.replace('data-track-', '')] = attr.value
        }
      })
      trackEvent(eventName, eventData)
    }
  })

  // Track visibility (tab switches)
  document.addEventListener('visibilitychange', () => {
    trackEvent('visibility_change', { hidden: document.hidden })
  })

  // Track beforeunload
  window.addEventListener('beforeunload', () => {
    trackEvent('page_unload', {
      time_on_page_ms: performance.now(),
    })
  })
}
